import type { Color } from './board.ts';
import { hashPosition, positionKey } from './hash.ts';
import { moveToSan, moveToUci, parseFen, toFen, uciToMove } from './notation.ts';
import { type Rules, draw, insufficientMaterial, rulesFor } from './rules.ts';
import type { EndReason, GameStatus, MoveEvent, Position, Variant } from './types.ts';

export interface MoveRecord {
  move: number;
  uci: string;
  san: string;
  color: Color;
  /** Piece taken by the move itself (0 if none). Variant effects are in `events`. */
  captured: number;
  events: MoveEvent[];
}

export const REASON_TEXT: Record<string, string> = {
  checkmate: 'Checkmate',
  stalemate: 'Stalemate',
  repetition: 'Threefold repetition',
  'fifty-move': '50-move rule',
  insufficient: 'Insufficient material',
  'king-captured': 'The king was captured',
  resign: 'Resignation',
  timeout: 'Time out',
  'timeout-draw': 'Time out, but the other side can’t mate: draw',
  abandon: 'Player left the game',
  agreement: 'Draw agreed',
  aborted: 'No result',
};

export function reasonText(variant: Variant | null, reason: EndReason | undefined): string {
  if (!reason) return '';
  return variant?.reasonText?.[reason] ?? REASON_TEXT[reason] ?? reason;
}

/** A game with history, undo and end-of-game detection, for any variant. */
export class Game {
  readonly variant: Variant;
  readonly rules: Rules;
  readonly startFen: string;
  private positions: Position[];
  private keys: string[];
  readonly history: MoveRecord[] = [];
  private legalCache: number[] | null = null;

  constructor(variant: Variant, fen: string = variant.startFen) {
    this.variant = variant;
    this.rules = rulesFor(variant);
    const start = parseFen(this.rules, fen);
    this.startFen = toFen(this.rules, start);
    this.positions = [start];
    this.keys = [positionKey(this.rules, start)];
  }

  static fromMoves(variant: Variant, uciMoves: readonly string[], fen?: string): Game {
    const game = new Game(variant, fen);
    for (const uci of uciMoves) {
      if (!game.play(uci)) throw new Error(`Illegal move in history: ${uci}`);
    }
    return game;
  }

  get position(): Position {
    return this.positions[this.positions.length - 1];
  }

  get turn(): Color {
    return this.position.turn;
  }

  get ply(): number {
    return this.history.length;
  }

  positionAt(ply: number): Position {
    return this.positions[Math.max(0, Math.min(ply, this.positions.length - 1))];
  }

  fen(): string {
    return toFen(this.rules, this.position);
  }

  legalMoves(): number[] {
    this.legalCache ??= this.rules.legalMoves(this.position);
    return this.legalCache;
  }

  /** h1 hashes of every position before the current one, for the AI's repetition detection. */
  historyHashes(): number[] {
    return this.positions.slice(0, -1).map((p) => hashPosition(this.rules, p).h1);
  }

  positionKeys(): readonly string[] {
    return this.keys;
  }

  play(move: string | number): MoveRecord | null {
    const pos = this.position;
    const legal = this.legalMoves();
    const m = typeof move === 'number' ? (legal.includes(move) ? move : null) : uciToMove(this.rules, pos, move, legal);
    if (m === null) return null;
    const events: MoveEvent[] = [];
    const next = this.rules.apply(pos, m, events);
    if (!next) return null;
    const captured = events.find((e) => e.t === 'remove' && e.cause === 'capture');
    const record: MoveRecord = {
      move: m,
      uci: moveToUci(m),
      san: moveToSan(this.rules, pos, m, legal),
      color: pos.turn,
      captured: captured && captured.t === 'remove' ? captured.piece : 0,
      events,
    };
    this.positions.push(next);
    this.keys.push(positionKey(this.rules, next));
    this.history.push(record);
    this.legalCache = null;
    return record;
  }

  undo(): MoveRecord | null {
    if (!this.history.length) return null;
    this.positions.pop();
    this.keys.pop();
    this.legalCache = null;
    return this.history.pop() ?? null;
  }

  isCheck(): boolean {
    return this.rules.inCheck(this.position);
  }

  /** Check, or a variant threat (e.g. a snail about to land on the king). */
  isThreatened(): boolean {
    return this.rules.isThreatened(this.position);
  }

  status(): GameStatus {
    const pos = this.position;
    const rules = this.rules;
    const t = rules.terminal(pos);
    if (t) return t;
    const canMove = this.legalCache ? this.legalCache.length > 0 : rules.hasLegalMove(pos);
    if (!canMove) return rules.noMovesStatus(pos);
    const v = this.variant;
    if (this.rules.deadMaterialRule(pos) && insufficientMaterial(pos.board)) return draw('insufficient');
    if ((v.fiftyMoveRule ?? true) && pos.halfmove >= 100) return draw('fifty-move');
    if (v.repetition ?? true) {
      const key = this.keys[this.keys.length - 1];
      let seen = 0;
      for (const k of this.keys) if (k === key) seen++;
      if (seen >= 3) return draw('repetition');
    }
    return { over: false };
  }
}

